/**
 * PianoKeyboard — visual piano keyboard.
 *
 * Two roles:
 *   1. Display — highlight keys as audio plays (driven by AudioPlayer's onNoteStart).
 *   2. Input — let the user click a key to answer "play this scale degree".
 *
 * Both modes are supported simultaneously. The component is purely presentational;
 * the parent owns state about which keys are highlighted.
 */

import { type ReactNode, useMemo } from "react";

export interface PianoKeyboardProps {
  /** MIDI range to render. Default C3..C6 (37 keys). */
  fromMidi?: number;
  toMidi?: number;
  /** Set of MIDI numbers currently highlighted (e.g. during playback). */
  activeMidis?: ReadonlySet<number>;
  /** When provided, clicking a key calls this. */
  onKeyClick?: (midi: number) => void;
  /** Optional label shown on each C key. */
  showOctaveLabels?: boolean;
}

const WHITE_INDICES = [0, 2, 4, 5, 7, 9, 11];
const BLACK_INDICES = [1, 3, 6, 8, 10];

interface KeyDef {
  midi: number;
  isBlack: boolean;
  /** White-key index used for x positioning. */
  whiteIndex: number;
}

export function PianoKeyboard({
  fromMidi = 48,
  toMidi = 84,
  activeMidis,
  onKeyClick,
  showOctaveLabels = true,
}: PianoKeyboardProps): ReactNode {
  const { whites, blacks, whiteCount } = useMemo(() => buildKeys(fromMidi, toMidi), [fromMidi, toMidi]);

  const whiteWidth = 100 / whiteCount;
  const blackWidth = whiteWidth * 0.6;

  return (
    <div className="relative w-full" style={{ aspectRatio: `${whiteCount * 0.5} / 1` }}>
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="img"
        aria-label="Piano keyboard"
      >
        {whites.map((k) => (
          <g key={k.midi}>
            <rect
              data-midi={k.midi}
              x={k.whiteIndex * whiteWidth}
              y={0}
              width={whiteWidth}
              height={100}
              className={getKeyClass(k.midi, activeMidis, false)}
              style={{ cursor: onKeyClick ? "pointer" : "default", outline: "none" }}
              onClick={onKeyClick ? () => onKeyClick(k.midi) : undefined}
            />
            {showOctaveLabels && k.midi % 12 === 0 && (
              <text
                x={k.whiteIndex * whiteWidth + whiteWidth / 2}
                y={92}
                textAnchor="middle"
                className="fill-inkFaded pointer-events-none"
                style={{ fontSize: "8px" }}
              >
                C{Math.floor(k.midi / 12) - 1}
              </text>
            )}
          </g>
        ))}
        {whites.slice(0, -1).map((k) => (
          <line
            key={`div-${k.midi}`}
            x1={(k.whiteIndex + 1) * whiteWidth}
            y1={0}
            x2={(k.whiteIndex + 1) * whiteWidth}
            y2={100}
            className="stroke-ink/30"
            strokeWidth={0.3}
            style={{ pointerEvents: "none" }}
          />
        ))}
        {blacks.map((k) => {
          const x = blackXPosition(k, whites, whiteWidth, blackWidth);
          return (
            <rect
              key={k.midi}
              data-midi={k.midi}
              x={x}
              y={0}
              width={blackWidth}
              height={62}
              className={getKeyClass(k.midi, activeMidis, true)}
              style={{ cursor: onKeyClick ? "pointer" : "default" }}
              onClick={onKeyClick ? () => onKeyClick(k.midi) : undefined}
            />
          );
        })}
      </svg>
    </div>
  );
}

function buildKeys(fromMidi: number, toMidi: number): {
  whites: KeyDef[];
  blacks: KeyDef[];
  whiteCount: number;
} {
  const whites: KeyDef[] = [];
  const blacks: KeyDef[] = [];
  let whiteIndex = 0;
  for (let m = fromMidi; m <= toMidi; m++) {
    const pc = ((m % 12) + 12) % 12;
    const isBlack = BLACK_INDICES.includes(pc);
    if (isBlack) {
      blacks.push({ midi: m, isBlack: true, whiteIndex: -1 });
    } else if (WHITE_INDICES.includes(pc)) {
      whites.push({ midi: m, isBlack: false, whiteIndex });
      whiteIndex++;
    }
  }
  return { whites, blacks, whiteCount: whiteIndex };
}

function blackXPosition(
  black: KeyDef,
  whites: KeyDef[],
  whiteWidth: number,
  blackWidth: number,
): number {
  // Position black key between its preceding and following white keys.
  const precedingWhite = [...whites].reverse().find((w) => w.midi < black.midi);
  if (!precedingWhite) return 0;
  return (precedingWhite.whiteIndex + 1) * whiteWidth - blackWidth / 2;
}

function getKeyClass(
  midi: number,
  active: ReadonlySet<number> | undefined,
  isBlack: boolean,
): string {
  const isActive = active?.has(midi);
  if (isBlack) {
    return isActive ? "fill-success transition-colors" : "fill-ink transition-colors";
  }
  return isActive ? "fill-success transition-colors" : "fill-paper transition-colors";
}
